import * as React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Add from '@mui/icons-material/Add'
import { useTheme } from '@mui/material/styles'
import { COARSE_POINTER, TOUCH_TARGET_PX } from '../touch'
import type { CalendarView, SourceStatus } from '../../typings'
import type { EventFilters, FilterKey } from '../../hooks/useEventFilters'
import type { UseActivityTypeFilterResult } from '../../hooks/useActivityTypeFilter'
import { ViewToggle } from './ViewToggle'
import { NavButtons } from './NavButtons'
import { FilterChipRow } from './FilterChipRow'

type Props = {
  view: CalendarView
  onViewChange: (view: CalendarView) => void
  heading: string
  onToday: () => void
  onPrev: () => void
  onNext: () => void
  filters: EventFilters
  onToggleFilter: (key: FilterKey) => void
  googleConnected: boolean
  sourceStatus: SourceStatus[]
  /** SH-2102: the expandable Activities chip, pass-through to FilterChipRow. */
  activityTypeFilter: UseActivityTypeFilterResult
  /** Pass-through to FilterChipRow's Sales Center badge Retry. */
  onRetrySource?: () => void
  /** "New Event" handler — unwired until the create flow is hooked up. */
  onNewEvent?: () => void
}

/**
 * Two rows whose geometry belongs entirely to the container
 * (LAYOUT-ARCHITECTURE.md §5.2): TitleRow owns the heading, ControlRow owns the
 * view toggle + navigation (left) and the filter chips (right). No child sets
 * padding it does not own.
 */
export function CalendarToolbar({
  view,
  onViewChange,
  heading,
  onToday,
  onPrev,
  onNext,
  filters,
  onToggleFilter,
  googleConnected,
  sourceStatus,
  activityTypeFilter,
  onRetrySource,
  onNewEvent,
}: Props) {
  const theme = useTheme()

  return (
    <Box>
      {/* TitleRow */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 2.5,
        }}
      >
        <Box
          component="h2"
          sx={{
            margin: 0,
            fontFamily: theme.typography.h6.fontFamily,
            fontSize: '20px',
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {heading}
        </Box>
        {/* Primary action, right of the heading (SPEC-CalendarToolbar.md §1). */}
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<Add sx={{ fontSize: '15px !important' }} />}
          onClick={onNewEvent}
          data-test-id="calendar-new-event"
          sx={{
            height: 33,
            px: '12px',
            borderRadius: '6px',
            backgroundColor: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: '14px',
            fontWeight: 500,
            textTransform: 'none',
            '& .MuiButton-startIcon': { mr: '6px' },
            [COARSE_POINTER]: { height: TOUCH_TARGET_PX },
          }}
        >
          New Event
        </Button>
      </Box>

      {/* ControlRow */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 3,
          pb: 2.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexShrink: 0,
          }}
        >
          <ViewToggle view={view} onChange={onViewChange} />
          <NavButtons
            view={view}
            onToday={onToday}
            onPrev={onPrev}
            onNext={onNext}
          />
        </Box>

        <FilterChipRow
          filters={filters}
          onToggle={onToggleFilter}
          googleConnected={googleConnected}
          sourceStatus={sourceStatus}
          activityTypeFilter={activityTypeFilter}
          onRetrySource={onRetrySource}
        />
      </Box>
    </Box>
  )
}
